import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { FileText } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../components/layout';
import { Alert } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { API_BASE, api, errorMessage } from '../../lib/api';
import { can, useMe } from '../../lib/session';
import type { OrderDetail } from '../../lib/types';
import { formatDateTime, formatMoney, parseMoney } from '../../lib/utils';
import { statusVariant } from './list';

type Action = 'cancel' | 'refund' | 'bank' | null;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} />
      <CardContent className="text-sm">{children}</CardContent>
    </Card>
  );
}

function address(a: Record<string, string | undefined>) {
  return [
    a.company,
    [a.firstName, a.lastName].filter(Boolean).join(' '),
    a.line1,
    a.line2,
    [a.postalCode, a.city].filter(Boolean).join(' '),
    a.countryCode,
    a.phone,
    a.pickupPointId ? `📦 ${a.pickupPointId}` : undefined,
  ]
    .filter(Boolean)
    .join(', ');
}

export function OrderDetailPage({ orderId }: { orderId: string }) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const order = useQuery({
    queryKey: ['orders', 'detail', orderId],
    queryFn: () => api<OrderDetail>(`/orders/${orderId}`),
  });
  const [action, setAction] = useState<Action>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  if (order.error) return <Alert variant="destructive">{errorMessage(order.error)}</Alert>;
  const o = order.data;
  if (!o) return <p className="text-muted-foreground">{t('common.loading')}</p>;
  const money = (minor: number) => formatMoney(minor, o.currency, i18n.language);
  const paid = o.payments.reduce((s, p) => s + p.capturedMinor - p.refundedMinor, 0);
  const cancellable = ['NEW', 'PAYMENT_PENDING', 'PAID', 'RESERVED', 'PROCESSING'].includes(
    o.status,
  );

  /** Перевозчик без API трекинга: сотрудник отмечает доставку сам. */
  const markDelivered = async (shipmentId: string) => {
    try {
      await api(`/warehouse/shipments/${shipmentId}/tracking`, {
        method: 'POST',
        body: { status: 'delivered' },
      });
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const run = async () => {
    setBusy(true);
    setError(undefined);
    try {
      if (action === 'cancel') {
        await api(`/orders/${orderId}/cancel`, { method: 'POST', body: { reason } });
      } else if (action === 'refund') {
        await api(`/orders/${orderId}/refund`, {
          method: 'POST',
          body: { amountMinor: parseMoney(amount), reason },
        });
      } else if (action === 'bank') {
        await api(`/orders/${orderId}/confirm-bank-transfer`, {
          method: 'POST',
          body: { amountMinor: parseMoney(amount) },
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
      setAction(null);
      setAmount('');
      setReason('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={`${t('ord.order')} ${o.number}`}
        description={`${o.email}${o.placedAt ? ` · ${formatDateTime(o.placedAt, i18n.language)}` : ''}`}
        actions={
          <>
            {o.status === 'PAYMENT_PENDING' && can(me, 'orders.write') ? (
              <Button
                variant="outline"
                onClick={() => {
                  setAction('bank');
                  setAmount((o.totals.totalMinor / 100).toFixed(2));
                }}
              >
                {t('ord.confirmBank')}
              </Button>
            ) : null}
            {paid > 0 && can(me, 'orders.refund') ? (
              <Button
                variant="outline"
                onClick={() => {
                  setAction('refund');
                }}
              >
                {t('ord.refund')}
              </Button>
            ) : null}
            {cancellable && can(me, 'orders.cancel') ? (
              <Button
                variant="destructive"
                onClick={() => {
                  setAction('cancel');
                }}
              >
                {t('ord.cancel')}
              </Button>
            ) : null}
          </>
        }
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <Badge variant={statusVariant(o.status)}>{t(`ord.statuses.${o.status as 'NEW'}`)}</Badge>
        <Badge variant="outline">{t(`ord.paymentStatuses.${o.paymentStatus as 'paid'}`)}</Badge>
        <Badge variant="outline">{t(`inv.taxModes.${o.taxMode as 'standard'}`)}</Badge>
        {o.vatId ? <Badge variant="outline">VAT {o.vatId}</Badge> : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="grid gap-4 lg:col-span-2">
          <Section title={t('ord.items')}>
            <table className="w-full">
              <tbody>
                {o.items.map((i) => (
                  <tr key={i.id} className="border-b last:border-0">
                    <td className="py-1.5">
                      {i.name} <span className="text-xs text-muted-foreground">{i.sku}</span>
                    </td>
                    <td className="text-right">{i.qty} ×</td>
                    <td className="text-right">{money(i.unitPriceMinor)}</td>
                    <td className="text-right font-medium">{money(i.totalMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-right">
              {t('ord.vat')}: {money(o.totals.taxMinor)} · <b>{money(o.totals.totalMinor)}</b>
            </p>
          </Section>

          <Section title={t('ord.sourcing')}>
            {o.sourcing.length === 0 ? (
              <p className="text-muted-foreground">{t('ord.noSourcing')}</p>
            ) : (
              <ul className="grid gap-2">
                {o.sourcing.map((s) => (
                  <li key={s.id}>
                    <span className="font-mono">{s.sku}</span> × {s.qty} →{' '}
                    {s.unresolvedReason ? (
                      <Badge variant="destructive">
                        {t('ord.manual', { reason: s.unresolvedReason })}
                      </Badge>
                    ) : s.chosenType === 'location' ? (
                      t('ord.ownStock')
                    ) : (
                      s.supplierName
                    )}
                    {s.candidates.length ? (
                      <details className="mt-1 text-xs text-muted-foreground">
                        <summary>{t('ord.candidates', { count: s.candidates.length })}</summary>
                        <ul className="ml-4 list-disc">
                          {s.candidates.map((c) => (
                            <li key={c.offerId}>
                              {c.excluded
                                ? t('ord.excluded', { reason: c.excluded })
                                : t('ord.score', { score: (c.score ?? 0).toFixed(2) })}
                              {c.landedCostMinor !== undefined
                                ? ` · ${money(c.landedCostMinor)}`
                                : ''}
                              {c.leadTimeDays !== undefined ? ` · ${String(c.leadTimeDays)} d` : ''}
                              {c.availableQty !== undefined
                                ? ` · ${String(c.availableQty)} pcs`
                                : ''}
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title={t('ord.timeline')}>
            <ol className="grid gap-1.5">
              {o.timeline.map((e, i) => (
                <li key={i} className="grid grid-cols-[9rem_6rem_1fr] gap-2">
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(e.at, i18n.language)}
                  </span>
                  <Badge variant="outline" className="justify-self-start">
                    {t(`ord.kinds.${e.kind}`)}
                  </Badge>
                  <span>
                    {e.title}
                    {e.detail ? <span className="text-muted-foreground"> — {e.detail}</span> : null}
                  </span>
                </li>
              ))}
            </ol>
          </Section>
        </div>

        <div className="grid content-start gap-4">
          <Section title={t('ord.delivery')}>
            <p>{address(o.shippingAddress)}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              {t('ord.billing')}: {address(o.billingAddress)}
            </p>
            <p className="mt-2">
              <Link
                to="/customers/$customerId"
                params={{ customerId: o.customerId }}
                className="text-primary hover:underline"
              >
                {t('ord.customerCard')}
              </Link>
            </p>
          </Section>
          <Section title={t('ord.payments')}>
            {o.payments.map((p) => (
              <p key={p.id}>
                {p.provider}:{p.method} · {p.status} · {money(p.capturedMinor)}
                {p.refundedMinor ? ` (−${money(p.refundedMinor)})` : ''}
              </p>
            ))}
            {o.refunds.map((r) => (
              <p key={r.id} className="text-muted-foreground">
                {t('ord.refundRow', { amount: money(r.amountMinor), status: r.status })}
              </p>
            ))}
          </Section>
          {o.purchaseOrders.length || o.shipments.length ? (
            <Section title={t('ord.fulfillment')}>
              {o.purchaseOrders.map((po) => (
                <p key={po.id}>
                  {po.number} · {po.supplierName} · {t(`po.statuses.${po.status as 'draft'}`)}
                </p>
              ))}
              {o.shipments.map((s) => (
                <p key={s.id}>
                  {s.carrier} · {s.status}
                  {s.trackingNumber ? ` · ${s.trackingNumber}` : ''}
                  {s.locationCode ? ` · ${s.locationCode}` : ''}
                  {(s.status === 'shipped' || s.status === 'in_transit') &&
                  can(me, 'fulfillment.ship') ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-2"
                      onClick={() => void markDelivered(s.id)}
                    >
                      {t('ord.markDelivered')}
                    </Button>
                  ) : null}
                </p>
              ))}
            </Section>
          ) : null}
          {o.documents.length ? (
            <Section title={t('ord.documents')}>
              {o.documents.map((d) => (
                <p key={d.id}>
                  <a
                    href={`${API_BASE}/invoices/${d.id}/pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <FileText className="size-4" />
                    {t(`inv.types.${d.type}`)} {d.fullNumber}
                  </a>
                </p>
              ))}
            </Section>
          ) : null}
          {o.returns.length ? (
            <Section title={t('ret.title')}>
              {o.returns.map((r) => (
                <p key={r.number}>
                  <Link to="/returns" className="hover:underline">
                    {r.number}
                  </Link>{' '}
                  · {t(`ret.statuses.${r.status as 'requested'}`)}
                </p>
              ))}
            </Section>
          ) : null}
        </div>
      </div>

      {action ? (
        <Dialog
          open
          onClose={() => {
            setAction(null);
          }}
          title={
            action === 'cancel'
              ? t('ord.cancel')
              : action === 'refund'
                ? t('ord.refund')
                : t('ord.confirmBank')
          }
          description={
            action === 'cancel'
              ? t('ord.cancelHint')
              : action === 'refund'
                ? t('ord.refundHint', { paid: money(paid) })
                : t('ord.confirmBankHint')
          }
          footer={
            <Button
              variant={action === 'cancel' ? 'destructive' : 'default'}
              disabled={
                busy ||
                (action !== 'bank' && reason.trim().length < 2) ||
                (action !== 'cancel' && !parseMoney(amount))
              }
              onClick={() => void run()}
            >
              {t('common.save')}
            </Button>
          }
        >
          <div className="grid gap-3">
            {action !== 'cancel' ? (
              <Field label={t('ord.amount')}>
                {(id) => (
                  <Input
                    id={id}
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                    }}
                  />
                )}
              </Field>
            ) : null}
            {action !== 'bank' ? (
              <Field label={t('ord.reason')}>
                {(id) => (
                  <Input
                    id={id}
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                    }}
                  />
                )}
              </Field>
            ) : null}
            {error ? <Alert variant="destructive">{error}</Alert> : null}
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
